import { Graphics, type Container } from "pixi.js";
import type { WireProjectileContact, WireSimState } from "@pit/protocol";
import {
  projectileImpactVisualFor,
  type ProjectileImpactProfile,
} from "../champions/projectileVisuals.js";
import { worldToScreen, type ProjectionCenter } from "./projection.js";
import {
  drawProjectileImpactGraphics,
  PROJECTILE_IMPACT_STYLES,
  type ProjectileImpactKind,
} from "./projectileImpactGraphics.js";

type WireEntity = WireSimState["entities"][number];
interface Point {
  readonly x: number;
  readonly y: number;
}

/** Displayed body anchor in world coordinates, with a zoom-1 screen height. */
export interface ProjectileImpactAnchor extends Point {
  readonly heightPx: number;
}

export const MAX_PROJECTILE_IMPACTS = 64;
export const CONTACT_DEDUPE_WINDOW_TICKS = 120;
export const MAX_CONTACT_DEDUPE_KEYS = 4096;

interface ImpactInstance {
  readonly order: number;
  readonly kind: ProjectileImpactKind;
  readonly worldX: number;
  readonly worldY: number;
  readonly entityId: string | null;
  readonly spawnedMs: number;
}

interface Slot {
  readonly graphics: Graphics;
  instance: ImpactInstance | null;
}

/** Authoritative, ephemeral contact presentation. No projectile reconciliation,
 * simulation or damage-audio ownership lives here. */
export class ProjectileImpactManager {
  private readonly slots: Slot[] = [];
  private readonly seen = new Map<string, number>();
  private readonly maxConcurrent: number;
  private lastSnapshotTick = -1;
  private discardedThroughTick = -1;
  private nextOrder = 0;
  private destroyed = false;

  constructor(
    private readonly layer: Container,
    maxConcurrent = MAX_PROJECTILE_IMPACTS,
  ) {
    if (!Number.isInteger(maxConcurrent) || maxConcurrent < 1) {
      throw new RangeError("ProjectileImpactManager requires a positive integer capacity");
    }
    this.maxConcurrent = Math.min(maxConcurrent, MAX_PROJECTILE_IMPACTS);
  }

  /** Returns whether one quiet terrain cue qualifies for this snapshot.
   * Call once per received snapshot, using its own entity visibility policy. */
  ingest(
    state: WireSimState,
    nowMs: number,
    isHidden: (entity: WireEntity) => boolean = () => false,
  ): boolean {
    if (this.destroyed || state.tick <= this.lastSnapshotTick) return false;
    this.lastSnapshotTick = state.tick;
    const oldestTick = state.tick - CONTACT_DEDUPE_WINDOW_TICKS;
    for (const [key, tick] of this.seen) {
      if (tick < oldestTick) this.seen.delete(key);
    }

    let terrainCue = false;
    const discardedBeforeSnapshot = this.discardedThroughTick;
    for (const contact of state.projectileContacts ?? []) {
      if (contact.simulationTick < oldestTick || contact.simulationTick > state.tick) continue;
      if (!this.rememberContact(contact, discardedBeforeSnapshot)) continue;
      const profile = projectileImpactVisualFor(contact.abilityId);
      let visibleOutcome = false;
      let trackedOutcome = false;
      for (const outcome of contact.outcomes) {
        const hasOutcome = outcome.damage > 0 || outcome.absorbed > 0 || outcome.immune;
        trackedOutcome ||= hasOutcome;
        if (!hasOutcome) continue;
        const entity = state.entities.find((candidate) => candidate.entityId === outcome.target);
        if (entity === undefined || isHidden(entity)) continue;
        visibleOutcome = true;
        if (outcome.damage > 0) this.spawn("damage", entity, profile, nowMs, outcome.target);
        if (outcome.absorbed > 0) this.spawn("absorb", entity, profile, nowMs, outcome.target);
        if (outcome.immune) this.spawn("immune", entity, profile, nowMs, outcome.target);
      }

      // An independently visible AoE victim above may render even when the
      // primary target/owner below is missing or concealed. Never substitute
      // that victim as permission to reveal a hidden contact or effect origin.
      const anchorEntity =
        contact.kind === "entity"
          ? state.entities.find((entity) => entity.entityId === contact.target)
          : state.entities.find((entity) => entity.seat === contact.ownerSeat);
      if (anchorEntity === undefined || isHidden(anchorEntity)) continue;

      if (contact.continuation === "reflected") {
        this.spawn("reflect", contact.contactPoint, profile, nowMs);
      } else {
        switch (contact.kind) {
          case "entity":
            if (!trackedOutcome) this.spawn("neutral", contact.contactPoint, profile, nowMs);
            break;
          case "terrain":
          case "arenaBoundary":
            this.spawn("terrain", contact.contactPoint, profile, nowMs);
            if (!visibleOutcome) terrainCue = true;
            break;
          case "zoneAbsorbed":
            this.spawn("zone", contact.contactPoint, profile, nowMs);
            break;
          case "rangeExpired":
            if (contact.effectOrigin === null && !trackedOutcome) {
              this.spawn("expiry", contact.contactPoint, profile, nowMs);
            }
            break;
        }
      }
      if (contact.effectOrigin !== null) {
        this.spawn("origin", contact.effectOrigin, profile, nowMs);
      }
    }
    return terrainCue;
  }

  private rememberContact(
    contact: WireProjectileContact,
    discardedBeforeSnapshot: number,
  ): boolean {
    if (contact.simulationTick <= discardedBeforeSnapshot) return false;
    // The producer guarantees unique contact identities within each snapshot.
    // If an exceptional burst retires this causal tick, finish rendering its
    // current snapshot without retaining more keys. Exact within-array dedupe
    // is then best-effort; later snapshots still reject the entire retired tick.
    // This keeps memory bounded without losing the newest accents or audio.
    if (contact.simulationTick <= this.discardedThroughTick) return true;
    const key = JSON.stringify([
      contact.simulationTick,
      contact.projectileId,
      contact.contactIndex,
    ]);
    if (this.seen.has(key)) return false;
    if (this.seen.size >= MAX_CONTACT_DEDUPE_KEYS) {
      // Retire a whole oldest causal tick from history on an exceptional burst.
      // Its cutoff prevents replay on subsequent snapshots, not first delivery.
      let oldest = Number.POSITIVE_INFINITY;
      for (const tick of this.seen.values()) oldest = Math.min(oldest, tick);
      this.discardedThroughTick = Math.max(this.discardedThroughTick, oldest);
      for (const [seenKey, tick] of this.seen) {
        if (tick <= this.discardedThroughTick) this.seen.delete(seenKey);
      }
      if (contact.simulationTick <= this.discardedThroughTick) return true;
    }
    // Remember hidden contacts too: a later copy must not reveal old activity.
    this.seen.set(key, contact.simulationTick);
    return true;
  }

  private spawn(
    kind: ProjectileImpactKind,
    anchor: Point,
    profile: ProjectileImpactProfile,
    nowMs: number,
    entityId: string | null = null,
  ): void {
    const slot = this.acquireSlot();
    slot.instance = {
      order: this.nextOrder++,
      kind,
      worldX: anchor.x,
      worldY: anchor.y,
      entityId,
      spawnedMs: nowMs,
    };
    drawProjectileImpactGraphics(slot.graphics, kind, profile);
    slot.graphics.alpha = 1;
    slot.graphics.scale.set(this.localRadius(PROJECTILE_IMPACT_STYLES[kind].startRadiusPx));
    slot.graphics.visible = true;
  }

  private acquireSlot(): Slot {
    for (const slot of this.slots) {
      if (slot.instance === null) return slot;
    }
    if (this.slots.length < this.maxConcurrent) {
      const graphics = new Graphics();
      graphics.visible = false;
      this.layer.addChild(graphics);
      const slot: Slot = { graphics, instance: null };
      this.slots.push(slot);
      return slot;
    }
    // Capacity is validated above, so the pool contains at least one slot.
    let oldest = this.slots[0];
    if (oldest === undefined) throw new Error("Projectile impact pool is unexpectedly empty");
    for (const slot of this.slots) {
      const oldestInstance = oldest.instance;
      if (
        slot.instance !== null &&
        (oldestInstance === null || slot.instance.order < oldestInstance.order)
      ) {
        oldest = slot;
      }
    }
    return oldest;
  }

  /** Transform existing meshes only; no new Graphics or geometry per frame. */
  update(
    nowMs: number,
    center: ProjectionCenter,
    entityAnchor?: (entityId: string) => ProjectileImpactAnchor | null,
  ): void {
    for (const slot of this.slots) {
      const instance = slot.instance;
      if (instance === null) continue;
      const style = PROJECTILE_IMPACT_STYLES[instance.kind];
      const progress = Math.max(0, (nowMs - instance.spawnedMs) / style.lifetimeMs);
      if (progress >= 1) {
        slot.instance = null;
        slot.graphics.visible = false;
        continue;
      }
      // Outcomes follow the body actually on screen (including interpolation),
      // not the newer snapshot's ground position. World-contact marks stay fixed.
      const body =
        instance.entityId !== null && entityAnchor !== undefined
          ? entityAnchor(instance.entityId)
          : undefined;
      if (body === null) {
        slot.graphics.visible = false;
        continue;
      }
      slot.graphics.visible = true;
      const screen = worldToScreen(body?.x ?? instance.worldX, body?.y ?? instance.worldY, center);
      slot.graphics.position.set(screen.sx, screen.sy - (body?.heightPx ?? 0) * center.zoom);
      // Hold the initial flash long enough to read before its short fade.
      slot.graphics.alpha = 1 - Math.max(0, (progress - 0.2) / 0.8);
      slot.graphics.scale.set(
        this.localRadius(
          style.startRadiusPx + (style.endRadiusPx - style.startRadiusPx) * progress,
        ),
      );
    }
  }

  /** The live fx layer carries viewport zoom; keep accents in screen pixels
   * while allowing the parent transform to move their world-space anchors. */
  private localRadius(screenRadius: number): number {
    const zoom = Math.abs(this.layer.scale.x);
    return zoom > 0 ? screenRadius / zoom : screenRadius;
  }

  get activeCount(): number {
    let count = 0;
    for (const slot of this.slots) if (slot.instance !== null) count++;
    return count;
  }

  get allocatedCount(): number {
    return this.slots.length;
  }

  get dedupeCount(): number {
    return this.seen.size;
  }

  destroy(): void {
    for (const slot of this.slots) slot.graphics.destroy();
    this.slots.length = 0;
    this.seen.clear();
    this.lastSnapshotTick = -1;
    this.discardedThroughTick = -1;
    this.destroyed = true;
  }
}
